import globals from '~/globals';
import Drawer from 'Components/Drawer';
import Panel from 'Components/Panel';
import SimpleList from 'Components/SimpleList';
import UptimeStatus from 'Components/UptimeStatus';
import { actions as deviceActions } from 'ducks/devices';
import ShowEquipment from 'Pages/Equipment/Show';
import deactivateDeviceButton from 'Pages/Tree/Device/deactivateDeviceButton';
import EditDevice from 'Pages/Tree/Device/Edit';
import KnownProblem from 'Pages/Tree/Device/KnownProblem';
import MoveChildren from 'Pages/Tree/Device/MoveChildren';
import ReplaceDevice from 'Pages/Tree/Device/ReplaceDevice';
import CreateTemplate from 'Pages/Tree/Device/WiseBox/CreateTemplate';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { unixToDate } from 'utils/date';
import displayMetadata from 'utils/displayMetadata';
import formatDescription from 'utils/formatDescription';
import statusToComponent from 'utils/statusToComponent';
import { FOREVER_TIMESTAMP } from 'utils/ui/constants';
import icons from 'utils/ui/icons';

const MOVABLE_OUT_PROTOCOLS = ['1-Wire', 'Mbus', 'Mbus TCP/IP', 'Modbus', 'Modbus TCP/IP'];

const generateProperties = (device, setOpenDrawer) => {
    let properties = [
        { label: 'ID', content: device.id },
        { label: 'Name', content: device.name },
        { label: 'Description', content: formatDescription(device.description) },
        { label: 'Installation Zone', content: device.installation_zone_full_path },
        { label: 'Creation', content: `${device.creator_name} @ ${unixToDate(device.created_at)}` },
        { label: 'Last Modification', content: `${device.updater_name} @ ${unixToDate(device.updated_at)}` },
        { label: 'Maximum Allowed Delay', content: `${device.max_delay / 3600}h` },
        { label: 'Status', content: statusToComponent(device.status) },
        {
            label: 'Equipment',
            content: (
                <a href="#" onClick={event => {
                    event.preventDefault();
                    setOpenDrawer('showEquipment');
                }}>
                    {device.equipment_name}
                </a>
            )
        }
    ];

    if (!device.parent_id) {
        properties.push({ label: 'Uptime', content: <UptimeStatus uptime={device.uptime}/> });
    }

    if (device.old_wise_box) {
        properties.push({ label: 'MAC Address', content: device.mac_address });
    }

    if (!device.equipment_name.startsWith('Wise')) {
        properties.push({ label: 'Integration?', content: device.integration ? 'Yes' : 'No' });
    }

    if (device.ti_current) {
        properties.push({ label: 'TI Current', content: device.ti_current });
    }

    if (device.ti_type) {
        properties.push({ label: 'TI Type', content: device.ti_type });
    }

    return [...properties, ...displayMetadata(device)];
};

const Component = props => {
    const { childDevices, device, installationZones, measurements } = props;

    const [drawer, setOpenDrawer] = useState(null);
    const closeDrawer = () => setOpenDrawer(null);

    const submitting = useSelector(state => state.devices.submitting);
    const currentUser = useSelector(state => state.users.models[state.auth.currentUserId]);

    const properties = generateProperties(device, setOpenDrawer);

    const
        hasChildDevices = !!childDevices.length,
        hasActiveIndicators = !!measurements.find(m => m.indicator_id && m.indicator_stop === FOREVER_TIMESTAMP);

    const dispatch = useDispatch();
    const { replace, regenerateSeedFiles, remove } = {
        replace: (id, equipmentId) => dispatch(deviceActions.replace(id, equipmentId)),
        regenerateSeedFiles: id => dispatch(deviceActions.regenerateSeedFiles(id)),
        remove: (id, redirect) => dispatch(deviceActions.remove(id, redirect))
    };

    const buttons = [
        {
            icon: icons.edit,
            onClick: () => setOpenDrawer('editDevice'),
            tooltip: 'Edit'
        },
        {
            disabled: !device.active,
            icon: icons.checkProblem,
            onClick: () => setOpenDrawer('knownProblem'),
            tooltip: 'Known Problem'
        }
    ];

    if (!hasActiveIndicators && !hasChildDevices) {
        buttons.push(
            {
                confirmationHeader: 'Delete Confirmation',
                confirmationText: 'Are you sure you want to delete?',
                submitting: submitting,
                icon: icons.trash,
                onClick: () => remove(device.id, `/sites/${device.site_id}`),
                tooltip: 'Delete'
            }
        );
    }

    if (currentUser?.superuser && device.status !== 'disabled') {
        buttons.push(deactivateDeviceButton(device, submitting, dispatch));
    }

    if (hasChildDevices && MOVABLE_OUT_PROTOCOLS.includes(device.out_protocol_name)) {
        buttons.push({
            icon: icons.move,
            onClick: () => setOpenDrawer('moveChildren'),
            tooltip: 'Move children to another parent'
        });
    }

    if (device.out_protocol_name === 'Reading Metal') {
        if (hasActiveIndicators) {
            buttons.push({
                icon: icons.replace,
                onClick: () => setOpenDrawer('replaceDevice'),
                tooltip: 'Replace device'
            });
        } else {
            buttons.push({
                confirmationHeader: 'Replace Confirmation',
                confirmationText: 'Are you sure you want to mark device as replaced?',
                submitting: submitting,
                icon: icons.replace,
                onClick: () => replace(device.id),
                tooltip: 'Mark device as replaced'
            });
        }
    }

    if (device.old_wise_box) {
        buttons.push(
            {
                confirmationHeader: 'Replace Confirmation',
                confirmationText: 'Are you sure you want change this to NewWiseBox? This process is irreversible.',
                submitting: submitting,
                icon: icons.unclaimed,
                onClick: () => replace(device.id),
                tooltip: 'Change to NewWiseBox'
            },
            {
                icon: icons.readConf,
                onClick: () => window.open(`${globals.seedUrl}/${device.id}/read_conf`),
                tooltip: 'Read configuration'
            },
            {
                icon: icons.actuators,
                onClick: () => window.open(`${globals.seedUrl}/${device.id}/act_conf`),
                tooltip: 'Actuation configuration'
            },
            {
                icon: icons.rulesConf,
                onClick: () => window.open(`${globals.seedUrl}/${device.id}/rules`),
                tooltip: 'Schedules configuration'
            },
            {
                confirmationHeader: 'Operation Confirmation',
                confirmationText: 'Are you sure you want to regenerate?',
                submitting: submitting,
                icon: icons.refresh,
                onClick: () => regenerateSeedFiles(device.id),
                tooltip: 'Regenerate configuration files'
            },
            {
                icon: icons.templateCreate,
                onClick: () => setOpenDrawer('createTemplate'),
                tooltip: 'Create New Template'
            },
            {
                icon: icons.pdf,
                onClick: () => window.open(`${globals.apiUrl}/devices/${device.id}/template`, '_blank'),
                tooltip: 'Download Installation Report'
            }
        );
    }

    let DrawerContent;
    if (drawer === 'createTemplate') {
        DrawerContent = (
            <CreateTemplate closeDrawer={closeDrawer} id={device.id}/>
        );
    } else if (drawer === 'editDevice') {
        DrawerContent = (
            <EditDevice closeDrawer={closeDrawer} id={device.id} installationZones={installationZones}/>
        );
    } else if (drawer === 'knownProblem') {
        DrawerContent = (
            <KnownProblem closeDrawer={closeDrawer} id={device.id}/>
        );
    } else if (drawer === 'moveChildren') {
        DrawerContent = (
            <MoveChildren childDevices={childDevices} device={device} onSave={closeDrawer}/>
        );
    } else if (drawer === 'replaceDevice') {
        DrawerContent = (
            <ReplaceDevice id={device.id} onSave={closeDrawer}/>
        );
    } else if (drawer === 'showEquipment') {
        DrawerContent = (
            <ShowEquipment disableButtons id={device.equipment_id}/>
        );
    }

    return (
        <React.Fragment>
            <Panel buttons={buttons}>
                <SimpleList content={properties}/>
            </Panel>
            <Drawer big={drawer === 'showEquipment'} onClose={closeDrawer} open={!!drawer}>
                {DrawerContent}
            </Drawer>
        </React.Fragment>
    );
};

export default Component;
